import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "profile-header-content" }
const _hoisted_2 = { class: "profile-header-top" }
const _hoisted_3 = { class: "profile-header-actions" }
const _hoisted_4 = {
  class: "drag-handle",
  title: "Drag to move"
}
const _hoisted_5 = { class: "profile-body" }

import { ref, computed, watch, onBeforeUnmount } from "vue";
import { Icon } from "@iconify/vue";
import LinkedInProfileTab from "@/components/bucket/linkedin/LinkedInProfileTab.vue";
import Unipile from "@/services/unipile/unipile";
import Contact from "@/services/contact/contact";

interface Props {
  show: boolean;
  selectedBucket?: {
    contact?: {
      linkedInUrl?: string;
      fname?: string;
      lname?: string;
      contactId?: string;
      profilePictureUrl?: string | null;
    };
  } | null;
  linkedInChannel?: {
    accountId?: string;
    status?: string;
  } | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LinkedInProfileViewerModal',
  props: {
    show: { type: Boolean, default: false },
    selectedBucket: { default: null },
    linkedInChannel: { default: null }
  },
  emits: ["close", "profile-picture-saved"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// Dragging state
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

// Profile state
const linkedInProfile = ref<any>(null);
const linkedInProfileLoading = ref(false);
const linkedInProfileError = ref<string | null>(null);
const linkedInProfileCache = ref<Record<string, any>>({});

// Dynamic positioning for drag functionality
const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};

  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
    width: "28%",
    minWidth: "350px",
    maxWidth: "500px",
    minHeight: "60vh",
    maxHeight: "85vh",
    borderRadius: "12px !important",
    zIndex: "10060",
  };
});

// Transform profile data to match LinkedInProfileTab format
const transformedProfile = computed(() => {
  if (!linkedInProfile.value) return null;

  const profile = linkedInProfile.value;
  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "";

  const certifications = (profile.certifications || []).map((cert: any) => ({
    name: cert.name || "",
    issuing_organization: cert.issuing_organization || cert.authority || "",
    issuingOrganization: cert.issuing_organization || cert.authority || "",
    issue_date:
      cert.issue_date ||
      (cert.start_date
        ? `${cert.start_date.month || 1}/${cert.start_date.day || 1}/${cert.start_date.year}`
        : undefined),
    issueDate:
      cert.issue_date ||
      (cert.start_date
        ? `${cert.start_date.month || 1}/${cert.start_date.day || 1}/${cert.start_date.year}`
        : undefined),
    expiration_date:
      cert.expiration_date ||
      (cert.end_date
        ? `${cert.end_date.month || 1}/${cert.end_date.day || 1}/${cert.end_date.year}`
        : null),
    expirationDate:
      cert.expiration_date ||
      (cert.end_date
        ? `${cert.end_date.month || 1}/${cert.end_date.day || 1}/${cert.end_date.year}`
        : null),
    credential_id: cert.credential_id || cert.license_number || "",
    credential_url: cert.credential_url || cert.url || "",
    url: cert.credential_url || cert.url || "",
  }));

  return {
    contactName: fullName,
    contactRole: profile.headline || "",
    location: profile.location || "",
    summary: profile.summary || "",
    profilePictureUrl: profile.profile_picture_url_large || profile.profile_picture_url || "",
    workExperience: profile.work_experience || [],
    education: profile.education || [],
    skills: profile.skills || [],
    languages: profile.languages || [],
    certifications: certifications,
    projects: profile.projects || [],
    volunteeringExperience: profile.volunteering_experience || [],
    recommendations: profile.recommendations || null,
  };
});

const throttledSections = computed(() => {
  return linkedInProfile.value?.throttled_sections || [];
});

// Drag handlers
const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [
    ".btn-close-profile",
    ".profile-body",
    ".linkedin-profile-tab",
    ".profile-card",
    "a",
    "button",
    "input",
    "textarea",
  ];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 480) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  const dragEvents = [
    ["mousemove", handleDrag, { passive: false }],
    ["mouseup", stopDrag],
    ["touchmove", handleDrag, { passive: false }],
    ["touchend", stopDrag],
  ] as const;

  dragEvents.forEach(([event, handler, options]) => {
    document.addEventListener(event, handler as any, options);
  });
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;

  const dragEvents = ["mousemove", "mouseup", "touchmove", "touchend"];
  const handlers = [handleDrag, stopDrag, handleDrag, stopDrag];

  dragEvents.forEach((event, index) => {
    document.removeEventListener(event, handlers[index] as any);
  });
};

// Profile loading methods
const isChannelHealthyStatus = (status?: string): boolean => {
  if (!status) return false;
  const s = String(status).toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(s);
};

const extractLinkedInIdentifierFromUrl = (url?: string): string | null => {
  if (!url) return null;

  try {
    const urlObj = new URL(url);
    const path = urlObj.pathname;

    const inMatch = path.match(/\/in\/([^/]+)/i);
    if (inMatch?.[1]) return decodeURIComponent(inMatch[1]);

    const companyMatch = path.match(/\/company\/([^/]+)/i);
    if (companyMatch?.[1]) return decodeURIComponent(companyMatch[1]);

    const salesPeopleMatch = path.match(/\/sales\/people\/([^/]+)/i);
    if (salesPeopleMatch?.[1]) return decodeURIComponent(salesPeopleMatch[1]);

    const parts = path.split("/").filter(Boolean);
    if (parts.length === 0) return null;
    return decodeURIComponent(parts[parts.length - 1]);
  } catch (error) {
    console.error("Failed to parse LinkedIn URL:", error);
    return null;
  }
};

const loadLinkedInProfile = async () => {
  const linkedInUrl = props.selectedBucket?.contact?.linkedInUrl;
  if (!linkedInUrl) {
    linkedInProfileError.value = "Please add a LinkedIn URL to the contact first.";
    linkedInProfile.value = null;
    return;
  }

  if (!props.linkedInChannel?.accountId) {
    linkedInProfileError.value = "LinkedIn channel is not connected.";
    linkedInProfile.value = null;
    return;
  }

  if (!isChannelHealthyStatus(props.linkedInChannel?.status)) {
    linkedInProfileError.value = "LinkedIn channel is disconnected. Please reconnect first.";
    linkedInProfile.value = null;
    return;
  }

  const identifier = extractLinkedInIdentifierFromUrl(linkedInUrl);
  if (!identifier) {
    linkedInProfileError.value = "Invalid LinkedIn URL. Please update the contact.";
    linkedInProfile.value = null;
    return;
  }

  const cacheKey = `${identifier}-${props.linkedInChannel.accountId}`;
  if (linkedInProfileCache.value[cacheKey]) {
    linkedInProfile.value = linkedInProfileCache.value[cacheKey];
    linkedInProfileError.value = null;
    return;
  }

  try {
    linkedInProfileLoading.value = true;
    linkedInProfileError.value = null;

    const response = await Unipile.getProfile(identifier, {
      account_id: props.linkedInChannel.accountId,
    });

    const profileData = response?.data?.data;
    if (!profileData) {
      linkedInProfileError.value = "Profile data not available.";
      linkedInProfile.value = null;
      return;
    }

    linkedInProfileCache.value[cacheKey] = profileData;
    linkedInProfile.value = profileData;
    linkedInProfileError.value = null;

    // Auto-save profile picture in the background if contact doesn't have one yet
    const contactId = props.selectedBucket?.contact?.contactId;
    const alreadyHasPicture = !!props.selectedBucket?.contact?.profilePictureUrl;
    const pictureUrl = profileData.profile_picture_url_large || profileData.profile_picture_url;
    if (contactId && !alreadyHasPicture && pictureUrl) {
      saveProfilePicture(contactId, pictureUrl);
    }
  } catch (error: any) {
    console.error("Failed to load LinkedIn profile:", error);
    linkedInProfileLoading.value = false;

    const statusCode = error?.response?.status;
    const errorData = error?.response?.data;

    const getErrorMessage = () => {
      if (errorData?.providerError?.detail) return errorData.providerError.detail;
      if (errorData?.error?.body?.detail) return errorData.error.body.detail;
      if (errorData?.errorMessage) return errorData.errorMessage;
      if (errorData?.message) return errorData.message;
      if (errorData?.providerError?.title) return errorData.providerError.title;
      if (error?.message) return error.message;
      return null;
    };

    const extractedMessage = getErrorMessage();
    let errorMessage = "Failed to load LinkedIn profile.";

    switch (statusCode) {
      case 400:
        errorMessage = extractedMessage || "Invalid request. Please check the LinkedIn URL.";
        break;
      case 401:
        errorMessage =
          extractedMessage ||
          "Your LinkedIn account appears to be disconnected. Please reconnect your account.";
        break;
      case 403:
        errorMessage =
          extractedMessage ||
          "This feature is not available for your subscription or account type.";
        break;
      case 404:
        errorMessage = extractedMessage || "User not found. The requested profile was not found.";
        break;
      case 422:
        errorMessage = extractedMessage || "Invalid account for this feature.";
        break;
      case 429:
        errorMessage = extractedMessage || "Too many requests. Please wait a moment and try again.";
        break;
      case 502:
        if (extractedMessage?.toLowerCase().includes("account not found")) {
          errorMessage = "LinkedIn account not found. Please check your account settings.";
        } else {
          errorMessage = extractedMessage || "LinkedIn service is temporarily unavailable.";
        }
        break;
      case 500:
      case 503:
      case 504:
        errorMessage =
          extractedMessage ||
          "LinkedIn service is temporarily unavailable. Please try again later.";
        break;
      default:
        errorMessage =
          extractedMessage || error?.message || "An unexpected error occurred. Please try again.";
    }

    linkedInProfileError.value = errorMessage;
    linkedInProfile.value = null;
  } finally {
    linkedInProfileLoading.value = false;
  }
};

const close = () => {
  isInitialPosition.value = true;
  modalPosition.value = { x: 0, y: 0 };
  emit("close");
};

const saveProfilePicture = async (contactId: string, pictureUrl: string) => {
  try {
    const response = await Contact.saveProfilePicture(contactId, pictureUrl);
    if (response.data.status) {
      emit("profile-picture-saved", response.data.data.profilePictureUrl);
    }
  } catch {
    // Silent failure — profile picture is non-critical
  }
};

// Watch for show prop changes to load profile
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      loadLinkedInProfile();
    } else {
      // Reset state when modal closes
      linkedInProfileError.value = null;
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Transition, { name: "modal-fade" }, {
    default: _withCtx(() => [
      (__props.show)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass(["profile-modal-overlay", { 'dragging-active': !isInitialPosition.value }])
          }, [
            _createElementVNode("div", {
              ref_key: "modalRef",
              ref: modalRef,
              class: _normalizeClass(["profile-modal", { 'is-dragging': isDragging.value }]),
              style: _normalizeStyle(modalStyle.value),
              onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
              onMousedown: startDrag,
              onTouchstart: startDrag
            }, [
              _createElementVNode("div", {
                class: _normalizeClass(["profile-header", { draggable: true }])
              }, [
                _createElementVNode("div", _hoisted_1, [
                  _createElementVNode("div", _hoisted_2, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:linkedin",
                      width: "16",
                      height: "16",
                      class: "header-icon"
                    }),
                    _cache[1] || (_cache[1] = _createElementVNode("h4", { class: "profile-title" }, "LinkedIn Profile", -1))
                  ])
                ]),
                _createElementVNode("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:drag",
                      width: "16",
                      height: "16"
                    })
                  ]),
                  _createElementVNode("button", {
                    type: "button",
                    class: "btn-close-profile",
                    onClick: close
                  }, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:close",
                      width: "18",
                      height: "18"
                    })
                  ])
                ])
              ]),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(LinkedInProfileTab, {
                  profile: transformedProfile.value,
                  loading: linkedInProfileLoading.value,
                  error: linkedInProfileError.value,
                  "throttled-sections": throttledSections.value,
                  onRetry: loadLinkedInProfile
                }, null, 8, ["profile", "loading", "error", "throttled-sections"])
              ])
            ], 38)
          ], 2))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})